import React, { Children, ReactNode } from 'react';

import { TAlertTypes } from '@r1-frontend/ui-react/components/Alert/TAlertTypes';
import Button, { TButtonType } from '@r1-frontend/ui-react/components/buttons/button';
import Cookie from '@r1-frontend/ui-react/components/emoji/emoji/Cookie';
import { TSvgProps } from '@r1-frontend/ui-react/components/svg/createSvg';
import { InfoCircle, InfoCircleFlip, TickCircle } from '@r1-frontend/ui-react/components/svg/main';
import { IndentContainer, ListContainer } from '@r1-frontend/ui-react/experimental/containers';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';

import * as S from './styled';

export type TAlertIcon = (props: TSvgProps) => JSX.Element

export type TAlertProps = {
    type?: TAlertTypes,
    withShadow?: boolean,
    Icon?: TAlertIcon,
    children: ReactNode,
    className?: string,
}

type TAlertTypedMap<T> = {
    [key in TAlertTypes]: T
}

type TAlertStateMap = TAlertTypedMap<[TAlertIcon, COLORS]>

const getAlertState = (type: TAlertTypes) => {
    const map: TAlertStateMap = {
        error: [InfoCircle, COLORS.BgError],
        success: [TickCircle, COLORS.BgSuccess],
        info: [InfoCircleFlip, COLORS.BgSurface],
        transparent: [InfoCircleFlip, COLORS.transparent],
        white: [Cookie, COLORS.BgMain],
    };

    return map[type];
};

const Alert = ({ Icon, children, type = 'info', withShadow = false, ...props }: TAlertProps): JSX.Element => {
    const [PredifinedIcon, background] = getAlertState(type);
    const [content, buttons] = Children.toArray(children);

    return (
        <S.Alert
            $radius={32}
            type={type}
            withShadow={withShadow}
            background={background}
            className={props.className}
        >
            <ListContainer $size={8}>
                <S.AlertIcon>
                    {Icon ? <Icon /> : <PredifinedIcon />}
                </S.AlertIcon>
                {content}
            </ListContainer>
            {buttons}
        </S.Alert>
    );
};

export const AlertButtons = ({ children }: { children: ReactNode }) => {
    if (Children.count(children) > 2) {
       throw new Error('@r1-frontend/ui-react. The "AlertButtons" cannot contain more than 2 buttons');
    }

    return (
        <S.AlertButtons>
            <IndentContainer $desktop={[0, 0, 0, 16]} $mobile={[12, 0, 0]}>
                <ListContainer data-test={'buttons-container'} $wrap={'nowrap'} $size={8}>
                    {children}
                </ListContainer>
            </IndentContainer>
        </S.AlertButtons>
    );
};

const getAlertPrimaryButtonType = (type: TAlertTypes) => {
    const map: TAlertTypedMap<TButtonType> = {
        error: 'action',
        success: 'primary',
        info: 'secondary',
        transparent: 'secondary',
        white: 'white',
    };

    return map[type];
};

export type TActionAlertButton = {
    label: string,
    onClick: () => void,
    btnType?: TButtonType,
    isLoading?: boolean,
};

type TActionAlert = {
    type: TAlertTypes,
    children: ReactNode,
}

type TActionAlertProps = TActionAlert & {
    button: TActionAlertButton,
};

type TConfirmAlertProps = TActionAlert & {
    buttons: Array<TActionAlertButton>,
};

export const ActionAlert = ({
    type,
    children,
    button: { label, onClick },
}: TActionAlertProps) => {
    return (
        <Alert type={type}>
            {children}
            <AlertButtons>
                <Button
                    data-test={'button'}
                    size={'small'}
                    bType={getAlertPrimaryButtonType(type)}
                    onClick={onClick}
                >
                    {label}
                </Button>
            </AlertButtons>
        </Alert>
    );
};

export const ConfirmAlert = ({ type, children, buttons }: TConfirmAlertProps) => {
    return (
        <Alert type={type}>
            {children}
            <AlertButtons>
                {buttons.map(({ label, onClick, btnType = 'white', isLoading }, index) => (
                    <Button
                        data-test={`button-${index}`}
                        key={index}
                        size={'small'}
                        bType={index ? btnType : getAlertPrimaryButtonType(type)}
                        onClick={onClick}
                        loading={isLoading}
                    >
                        {label}
                    </Button>
                ))}
            </AlertButtons>
        </Alert>
    );
};

export default Alert;
